import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ahtApi } from '../../network/apis/aht';
import { casesApi } from '../../network/apis/cases';
import '../../assets/css/doctor/doctor_dashboard.css'; // Reusing doctor dashboard styles
import RoleSwitcher from '../../components/common/RoleSwitcher';

// Material Design color palette
const materialColors = {
    primary: '#1976D2',
    secondary: '#424242',
    success: '#4CAF50',
    warning: '#FF9800',
    error: '#F44336',
    lightBg: '#F5F5F5',
    borderColor: '#E0E0E0',
    textPrimary: '#212121',
    textSecondary: '#757575',
    divider: '#BDBDBD'
};

const AHTDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [ahtProfile, setAHTProfile] = useState(null);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAHTData();
    }, []);

    const fetchAHTData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch AHT profile
            const profileResponse = await ahtApi.getAHTProfile();
            setAHTProfile(profileResponse?.data?.data || profileResponse?.data);
            
            // Fetch associated cases
            const casesResponse = await casesApi.getCases();
            setCases(casesResponse?.data?.data || casesResponse?.data || []);
        } catch (err) {
            console.error('Failed to fetch AHT data:', err);
            setError(err.message || 'Failed to fetch profile data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: materialColors.lightBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}>
                <div style={{
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: `4px solid ${materialColors.borderColor}`,
                        borderTop: `4px solid ${materialColors.primary}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                    }} />
                    <p style={{ color: materialColors.textSecondary }}>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: materialColors.lightBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '32px',
                    maxWidth: '500px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <div style={{
                        backgroundColor: '#FFEBEE',
                        border: `1px solid ${materialColors.error}`,
                        borderRadius: '4px',
                        padding: '16px',
                        marginBottom: '24px',
                        color: '#C62828'
                    }}>
                        {error}
                    </div>
                    <button 
                        onClick={fetchAHTData}
                        style={{
                            backgroundColor: materialColors.primary,
                            color: 'white',
                            padding: '10px 24px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Calculate statistics
    const totalCases = cases.length;
    const activeCases = cases.filter(c => 
        c.status === 'PENDING' || c.status === 'ASSIGNED' || c.status === 'IN_PROGRESS'
    ).length;
    const completedCases = cases.filter(c => 
        c.status === 'RESOLVED' || c.status === 'CANCELLED'
    ).length;

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: materialColors.lightBg,
            padding: '32px 16px',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        }}>
            <RoleSwitcher />
            
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header Section */}
                <div style={{ marginBottom: '48px' }}>
                    <h1 style={{
                        color: materialColors.textPrimary,
                        fontSize: '32px',
                        fontWeight: 500,
                        marginBottom: '8px',
                        marginTop: 0
                    }}>
                        Welcome, {user?.first_name || 'Animal Health Technologist'}!
                    </h1>
                    <p style={{
                        color: materialColors.textSecondary,
                        fontSize: '16px',
                        marginTop: '8px'
                    }}>
                        Animal Health Technologist Dashboard
                    </p>
                </div>

                {/* Verification Status */}
                {ahtProfile && !ahtProfile.is_verified && (
                    <div style={{
                        backgroundColor: '#FFF3E0',
                        border: `1px solid ${materialColors.warning}`,
                        borderRadius: '8px',
                        padding: '16px 16px',
                        marginBottom: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            fontSize: '24px'
                        }}>⚠️</div>
                        <div>
                            <p style={{
                                color: '#E65100',
                                fontSize: '14px',
                                fontWeight: 500,
                                marginBottom: '4px',
                                marginTop: 0
                            }}>
                                Verification Pending
                            </p>
                            <p style={{
                                color: '#BF360C',
                                fontSize: '13px',
                                marginTop: 0,
                                marginBottom: 0
                            }}>
                                Your registration is pending verification. Please check back soon!
                            </p>
                        </div>
                    </div>
                )}

                {/* Profile Card */}
                {ahtProfile && (
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        padding: '24px',
                        marginBottom: '32px',
                        border: ahtProfile.is_verified ? `2px solid ${materialColors.success}` : 'none'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '24px'
                        }}>
                            <div>
                                <h2 style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: '4px'
                                }}>
                                    {ahtProfile.user_details?.first_name} {ahtProfile.user_details?.last_name}
                                </h2>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '14px',
                                    marginTop: '4px',
                                    marginBottom: 0
                                }}>
                                    {ahtProfile.specialization}
                                </p>
                            </div>
                            <span style={{
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: ahtProfile.is_verified ? '#E8F5E9' : '#FFF9C4',
                                color: ahtProfile.is_verified ? '#2E7D32' : '#F57F17'
                            }}>
                                {ahtProfile.is_verified ? '✓ Verified' : '⋯ Pending'}
                            </span>
                        </div>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '20px',
                            borderTop: `1px solid ${materialColors.borderColor}`,
                            paddingTop: '24px'
                        }}>
                            <div>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    marginBottom: '8px',
                                    marginTop: 0,
                                    textTransform: 'uppercase'
                                }}>
                                    License Number
                                </p>
                                <p style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: 0
                                }}>
                                    {ahtProfile.license_number}
                                </p>
                            </div>
                            <div>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    marginBottom: '8px',
                                    marginTop: 0,
                                    textTransform: 'uppercase'
                                }}>
                                    Experience
                                </p>
                                <p style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: 0
                                }}>
                                    {ahtProfile.experience} years
                                </p>
                            </div>
                            <div>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    marginBottom: '8px',
                                    marginTop: 0,
                                    textTransform: 'uppercase'
                                }}>
                                    Coverage Area
                                </p>
                                <p style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: 0
                                }}>
                                    {ahtProfile.coverage_area}
                                </p>
                            </div>
                            <div>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    marginBottom: '8px',
                                    marginTop: 0,
                                    textTransform: 'uppercase'
                                }}>
                                    Consultation Fee
                                </p>
                                <p style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: 0
                                }}>
                                    ₦{parseFloat(ahtProfile.consultation_fee).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Statistics Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '32px',
                            color: materialColors.primary,
                            fontWeight: 500,
                            marginBottom: '8px'
                        }}>
                            {totalCases}
                        </div>
                        <p style={{
                            color: materialColors.textSecondary,
                            fontSize: '14px',
                            marginTop: 0,
                            marginBottom: 0
                        }}>
                            Total Cases
                        </p>
                    </div>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '32px',
                            color: materialColors.warning,
                            fontWeight: 5,
                            marginBottom: '8px'
                        }}>
                            {activeCases}
                        </div>
                        <p style={{
                            color: materialColors.textSecondary,
                            fontSize: '14px',
                            marginTop: 0,
                            marginBottom: 0
                        }}>
                            Active Cases
                        </p>
                    </div>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '32px',
                            color: materialColors.success,
                            fontWeight: 500,
                            marginBottom: '8px'
                        }}>
                            {completedCases}
                        </div>
                        <p style={{
                            color: materialColors.textSecondary,
                            fontSize: '14px',
                            marginTop: 0,
                            marginBottom: 0
                        }}>
                            Completed Cases
                        </p>
                    </div>
                </div>

                {/* Recent Cases Section */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    padding: '24px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <h3 style={{
                            color: materialColors.textPrimary,
                            fontSize: '18px',
                            fontWeight: 500,
                            marginTop: 0,
                            marginBottom: 0
                        }}>
                            Recent Cases
                        </h3>
                        <button 
                            onClick={() => navigate('/aht/cases')}
                            style={{
                                color: materialColors.primary,
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#1565C0'}
                            onMouseLeave={(e) => e.target.style.color = materialColors.primary}
                        >
                            View All
                        </button>
                    </div>
                    
                    {cases.length > 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {cases.slice(0, 5).map((caseItem, index) => (
                                <div 
                                    key={caseItem.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '16px',
                                        backgroundColor: index === 0 ? '#F5F5F5' : 'white',
                                        borderRadius: '4px',
                                        border: `1px solid ${materialColors.borderColor}`,
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#F5F5F5';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => navigate(`/aht/case/${caseItem.id}`)}
                                >
                                    <div>
                                        <p style={{
                                            color: materialColors.textPrimary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            marginTop: 0,
                                            marginBottom: '4px'
                                        }}>
                                            Case #{caseItem.id}
                                        </p>
                                        <p style={{
                                            color: materialColors.textSecondary,
                                            fontSize: '12px',
                                            marginTop: 0,
                                            marginBottom: 0
                                        }}>
                                            {new Date(caseItem.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            backgroundColor: caseItem.status === 'RESOLVED' ? '#E8F5E9' : 
                                                            caseItem.status === 'IN_PROGRESS' ? '#E3F2FD' : '#FFF9C4',
                                            color: caseItem.status === 'RESOLVED' ? '#2E7D32' : 
                                                   caseItem.status === 'IN_PROGRESS' ? '#1565C0' : '#F57F17'
                                        }}>
                                            {caseItem.status}
                                        </span>
                                        <span style={{
                                            color: materialColors.primary,
                                            fontSize: '18px'
                                        }}>
                                            →
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '48px 24px',
                            color: materialColors.textSecondary
                        }}>
                            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No cases assigned yet</p>
                            <p style={{ fontSize: '14px', marginTop: 0 }}>Check back soon for new assignments</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AHTDashboard;
